import {
	BottomTextLeft,
	BottomTextRight,
	CountText,
	HoursDaysMin,
	StyledButton,
	StyledCard,
	TimeFormat,
	Top,
	TopBar,
	TopSubText,
	TopText,
} from './StylovyzeCard.styles';
import React from 'react';

import StylovyzeCardFooter from './StylovyzeCardFooter';
import { minutesToDaysHoursMinutes } from '../../utils/timeFormatting';
import { useGlobalization } from '../../contexts/GlobalizationProvider';
import { useWeaveThemeContext } from '../../hocs/ApplicationWrapper/theme/WeaveContext';
import { StylovyzeCardProps, FormatNumberProps } from './StylovyzeCard.types';

// TODO: Add composable patterns support (Component.Root, Component.Item, etc.) for advanced usage
// TODO: Consider React.forwardRef and React.memo for performance optimization when needed

const FormatNumber = ({ count, format }: FormatNumberProps) => {
	const { t } = useGlobalization();
	if (count && typeof count === 'number' && format === 'time') {
		const time = minutesToDaysHoursMinutes(count);
		const showDays = time.days > 0;
		const showHours = showDays || time.hours > 0;
		const showMins = showHours || time.minutes > 0;
		return (
			<TimeFormat>
				{showDays && (
					<>
						<HoursDaysMin>{time.days}</HoursDaysMin>
						{t('days', { count: time.days })}
					</>
				)}
				{showHours && (
					<>
						<HoursDaysMin>{time.hours}</HoursDaysMin>
						{t('hours', { count: time.hours })}
					</>
				)}
				{showMins && (
					<>
						<HoursDaysMin>{time.minutes}</HoursDaysMin>
						{t('minutes', { count: time.minutes })}
					</>
				)}
			</TimeFormat>
		);
	}
	return <CountText>{count ?? '-'}</CountText>;
};

/**
 * StylovyzeCard provides a presentational card interface for displaying user data with customizable content zones.
 * Supports count display (number/text/time formatting), title/subtitle text, footer content, and optional action buttons.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <StylovyzeCard
 *   topText="Active Users"
 *   countText={1247}
 *   topSubText="Last 30 days"
 *   bottomTextLeft="Updated"
 *   bottomTextRight="2 hours ago"
 * />
 *
 * // Time formatting
 * <StylovyzeCard
 *   topText="Duration"
 *   countText={8800}
 *   countFormat="time"
 *   titlePosition="above"
 * />
 *
 * // With action button
 * <StylovyzeCard
 *   topText="Reports"
 *   countText={42}
 *   button={{
 *     title: 'Download CSV',
 *     onClick: handleDownload,
 *     icon: <DownloadIcon />
 *   }}
 * />
 * ```
 *
 * @warning This component requires useGlobalization and useWeaveThemeContext providers to be available in the component tree.
 *
 * @see {@link https://stylovyze.info360-dev.com/?path=/story/ds-tables-cards-stylovyzecard--primary | StylovyzeCard Storybook Documentation}
 *
 * @param props - Component props
 * @returns StylovyzeCard component
 */
const StylovyzeCard = ({
	topColor = '#007096',
	countFormat = 'number',
	fullHeight,
	height,
	dataCy,
	titlePosition,
	topText,
	countText,
	topSubText,
	bottomTextLeft,
	bottomTextRight,
	secondaryFooter,
	button,
}: StylovyzeCardProps): JSX.Element => {
	const { isWeaveTheme, weaveThemeConsumer } = useWeaveThemeContext();
	const resolvedRoles = weaveThemeConsumer?.resolvedRoles;

	return (
		<StyledCard
			$fullHeight={fullHeight ?? false}
			$height={height}
			data-cy={dataCy}>
			<TopBar $topColor={topColor} />
			<Top $titlePosition={titlePosition}>
				<FormatNumber count={countText} format={countFormat} />
				<TopText
					$titlePosition="above"
					$aboveColor={
						isWeaveTheme
							? resolvedRoles['modal.fontColor']
							: '#4a6067'
					}>
					{topText}
				</TopText>
				<TopSubText
					$titlePosition={titlePosition}
					$belowColor={
						isWeaveTheme
							? resolvedRoles['modal.fontColor']
							: '#4a6067'
					}>
					{topSubText}
				</TopSubText>
				{button && (
					<StyledButton
						variant="outlined"
						color="primary"
						onClick={button.onClick}
						{...button.props}>
						{button.icon} {button.title}
					</StyledButton>
				)}
			</Top>
			{(bottomTextLeft || bottomTextRight) && (
				<StylovyzeCardFooter>
					<BottomTextLeft>{bottomTextLeft}</BottomTextLeft>
					<BottomTextRight>{bottomTextRight}</BottomTextRight>
				</StylovyzeCardFooter>
			)}
			{secondaryFooter}
		</StyledCard>
	);
};

export default StylovyzeCard;
